import { useState, useRef, useCallback } from 'react';
import { Upload, Link as LinkIcon, X, Loader2, Check } from 'lucide-react';
import { getSupabaseAdminClient } from '@parentpath/supabase-client';

const supabase = getSupabaseAdminClient();

// Bucket types for type safety
export type StorageBucket = 'BlogTitleImages' | 'ProductImages' | 'PremiumFlowImages' | 'recommendation-images';

interface ImageUploadProps {
  value: string;
  onChange: (url: string) => void;
  bucket: StorageBucket;
  label?: string;
  placeholder?: string;
  className?: string;
}

// Generate a random filename with original extension
function generateFilename(originalName: string): string {
  const ext = originalName.split('.').pop()?.toLowerCase() || 'jpg';
  const randomId = Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
  return `${randomId}.${ext}`;
}

// Get the public URL for a file in a bucket
function getPublicUrl(bucket: StorageBucket, path: string): string {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  return `${supabaseUrl}/storage/v1/object/public/${bucket}/${path}`;
}

export function ImageUpload({
  value,
  onChange,
  bucket,
  label = 'Bild',
  placeholder = 'https://example.com/image.jpg',
  className = '',
}: ImageUploadProps) {
  const [mode, setMode] = useState<'url' | 'upload'>('url');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUrlChange = (url: string) => {
    setUploadError(null);
    onChange(url);
  };

  const handleFileUpload = useCallback(async (file: File) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setUploadError('Bitte nur Bilddateien hochladen');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setUploadError('Datei ist zu groß (max. 10MB)');
      return;
    }

    setIsUploading(true);
    setUploadError(null);

    try {
      const filename = generateFilename(file.name);

      const { error } = await supabase.storage
        .from(bucket)
        .upload(filename, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (error) {
        throw error;
      }

      // Get the public URL
      const publicUrl = getPublicUrl(bucket, filename);
      onChange(publicUrl);
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error instanceof Error ? error.message : 'Upload fehlgeschlagen');
    } finally {
      setIsUploading(false);
    }
  }, [bucket, onChange]);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileUpload(file);
    }
  }, [handleFileUpload]);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const clearImage = () => {
    onChange('');
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Label */}
      {label && (
        <label className="block text-sm font-medium text-slate-700">{label}</label>
      )}

      {/* Mode Toggle */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setMode('url')}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
            mode === 'url'
              ? 'bg-teal-500 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          <LinkIcon className="w-4 h-4" />
          URL eingeben
        </button>
        <button
          type="button"
          onClick={() => setMode('upload')}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
            mode === 'upload'
              ? 'bg-teal-500 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          <Upload className="w-4 h-4" />
          Hochladen
        </button>
      </div>

      {/* URL Input Mode */}
      {mode === 'url' && (
        <div className="relative">
          <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="url"
            value={value}
            onChange={(e) => handleUrlChange(e.target.value)}
            placeholder={placeholder}
            className="w-full pl-10 pr-10 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
          />
          {value && (
            <button
              type="button"
              onClick={clearImage}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {/* Upload Mode */}
      {mode === 'upload' && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
          className={`relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all ${
            dragActive
              ? 'border-teal-500 bg-teal-50'
              : isUploading
              ? 'border-slate-300 bg-slate-50'
              : 'border-slate-200 hover:border-teal-300 hover:bg-slate-50'
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileInputChange}
            className="hidden"
          />

          {isUploading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
              <p className="text-sm text-slate-600">Wird hochgeladen...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <Upload className="w-8 h-8 text-slate-400" />
              <p className="text-sm text-slate-600">
                <span className="text-teal-600 font-medium">Klicken</span> oder Datei hierher ziehen
              </p>
              <p className="text-xs text-slate-400">PNG, JPG, GIF, WebP (max. 10MB)</p>
            </div>
          )}
        </div>
      )}

      {/* Error Message */}
      {uploadError && (
        <p className="text-sm text-red-500 flex items-center gap-1">
          <X className="w-4 h-4" />
          {uploadError}
        </p>
      )}

      {/* Image Preview */}
      {value && (
        <div className="relative inline-block">
          <div className="relative rounded-lg overflow-hidden border border-slate-200 bg-slate-50">
            <img
              src={value}
              alt="Preview"
              className="max-w-xs max-h-40 object-contain"
              onError={(e) => {
                const img = e.target as HTMLImageElement;
                img.style.display = 'none';
                // Show error message in parent
                const parent = img.parentElement;
                if (parent && !parent.querySelector('.image-load-error')) {
                  const errorDiv = document.createElement('div');
                  errorDiv.className = 'image-load-error p-4 text-center';
                  errorDiv.innerHTML = `
                    <p class="text-sm text-red-500 mb-2">Bild konnte nicht geladen werden</p>
                    <p class="text-xs text-slate-400 break-all">${value}</p>
                  `;
                  parent.appendChild(errorDiv);
                }
              }}
              onLoad={(e) => {
                const img = e.target as HTMLImageElement;
                img.style.display = 'block';
                // Remove any error message
                const parent = img.parentElement;
                const errorDiv = parent?.querySelector('.image-load-error');
                if (errorDiv) errorDiv.remove();
              }}
            />
            <button
              type="button"
              onClick={clearImage}
              className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex items-center gap-1 mt-1 text-xs text-green-600">
            <Check className="w-3 h-3" />
            Bild ausgewählt
          </div>
          {/* Debug: Show URL */}
          <div className="mt-1">
            <a
              href={value}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-500 hover:underline break-all"
            >
              URL in neuem Tab öffnen
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
